import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import TimedRedirect from './TimedRedirect';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppContext } from '../libs/contextLib';
import {
  userStates,
  userTypes,
  isAccessPermitted,
  isAccessPermittedByDepartment
} from '../libs/userLib';
import { isAbsoluteURL } from '../libs/urllib';
import config from '../config';

/**
 * Route component that will look at the suer state in ther app context.
 * Will redirect to login (with current path as redirect) when user is not
 * AUTHORISED.
 * @param {int[]} authorisedTypes
 *    Array of all types that should have access to this route. If none are
 *    supplied all types have access.
 * @param {string[]} authorisedDepartments
 *    If the user is CFS staff, their department will be tested against this. If
 *    the prop is not set, all departments will have access.
 * @param {*} children 
 *    Children of the route component.
 */
export default function AuthenticatedRoute(
  {
    authorisedTypes = [userTypes.all],
    authorisedDepartments = ['all'],
    children
  }) {
  const { pathname, search } = useLocation();
  const { userState }        = useAppContext();
  const { userType }         = useAppContext();
  const { providerName }     = useAppContext();
  const { userDepartment }   = useAppContext();
  const { redirectInfo }     = useAppContext();

  /**
   * Build a "?redirect=<path>" parameter based on requested path/query."
   */
  function getRedirectParameter() {
    if (
      (
        pathname === '/' ||
        pathname === ''
      ) && search === ''
    ) {
      return '';
    }
    return (`?redirect=${pathname}${search}`);
  }

  /**
   * Return a redirect either to the login or confirm page.
   */
  function getRedirect() {
    // If a user is unconfirmed redirect to confirm.
    if (
      pathname === '/logout'
    ) {
      // Don't redirect back to logout after logging out the user.
      // Otherwise redirect them to login.
      const loginLink = config.links.loginUserType;
      if(isAbsoluteURL(loginLink)) {
        window.location = loginLink;
      }
      else {
        return (
          <Navigate to={
            loginLink
          } />
        );
      }
    }
    else if (userState === userStates.UNCONFIRMED) {
      // otherwise redirect the user bakc after signing in.
      return (
        <Navigate to={
          `/confirm${getRedirectParameter()}`
        } />
      );
    }
    // If a user is needs to change their password.
    else if (
      userState === userStates.FORCE_CHANGE_PASSWORD  &&
      pathname !== '/user/password'
    ) {
      return (
        <Navigate to={
          `/user/password${getRedirectParameter()}`
        } />
      );
    }
    // If a user needs to change their password and is already trying to, let
    // them.
    else if (
      userState === userStates.FORCE_CHANGE_PASSWORD  &&
      pathname === '/user/password'
    ) {
      return children;
    }
    // Redirect confirmed users that are undifferentiated to diff.
    else if (
      userState === userStates.CONFIRMED && 
      userType === userTypes.undiff
    ) {
      // Return the children if we're already at the correct path.
      // otherwise redirect them to settype.
      if (pathname === '/user/settype') {
        return children;
      }
      else {
        return (
          <Navigate to={
            `/user/settype${getRedirectParameter()}`
          } />
        );
      }
    }
    // Otherwise redirect them to login.
    let loginBase = config.links.loginUserType;
    // We may be able to figure out what type of user we're dealing with
    // from the redirect_uri.
    if (
      typeof redirectInfo.redirect === 'string' &&
      redirectInfo.redirect.includes('AdviserPortal')
    ) {
      loginBase = '/login/adviser';
    }
    else if(
      typeof redirectInfo.redirect === 'string' &&
      redirectInfo.redirect.includes('CustomerPortal')
    ) {
      loginBase = '/login/adviser';
    }
    const loginLink = `${loginBase}${getRedirectParameter()}`;
    if(isAbsoluteURL(loginLink)) {
      window.location = loginLink;
    }
    else {
      return (
        <Navigate to={
          loginLink
        } />
      );
    }
  };

  /**
   * Returns children if permissions match.
   */
  function getChildren() {
    let hasAccess = false;
    // Look at type and permissions.
    hasAccess = isAccessPermitted(userType, authorisedTypes)
    // test the department as well for staff.
    if(
      hasAccess && (
        userType & userTypes.staff
      )
    ) {
      hasAccess = isAccessPermittedByDepartment(providerName, userDepartment, authorisedDepartments)
    }
    if(hasAccess) {
      return (children);
    }
    else {
      return (
        <Container fluid="sm" className = "mt-3">
          <Row>
            <Col>
              <TimedRedirect
                className    = "mb-3"
                target       = '/'
                friendlyName = 'the homepage'
                message      = 'You do not have permission to access this page.'
              />
            </Col>
          </Row>
        </Container>
      );
    }
  }

  return (
    // Check whether a user needs to confirm.
    // And check whether they ened to differentiate their type.
    (
      userState === userStates.CONFIRMED && 
      userType !== userTypes.undiff
    ) ? (
      getChildren()
    ) : (
      getRedirect()    
    )
  );
}
